// ==========================
// Version 2 — mintOnChain.js
// - v1 FIX: Thirdweb v5 sendTransaction requires `account`
//   * Accepts connected Thirdweb `account` from useActiveAccount()
//   * sendTransaction({ transaction, account }) to avoid onTransactionRequested crash
//   * waitForReceipt({ client, chain, transactionHash }) (v5 shape)
// - Still supports minting to an explicit `to` address (defaults to account.address)
// ==========================

/* global BigInt */

import {
  createThirdwebClient,
  getContract,
  prepareContractCall,
  sendTransaction,
  waitForReceipt,
} from "thirdweb";
import { defineChain } from "thirdweb/chains";
import { NFT_COLLECTION_ADDRESS } from "../thirdweb.config";

// React env var (set in .env.local as REACT_APP_THIRDWEB_CLIENT_ID=...)
const THIRDWEB_CLIENT_ID = process.env.REACT_APP_THIRDWEB_CLIENT_ID;

if (!THIRDWEB_CLIENT_ID) {
  console.warn(
    "[BWK][CHAIN] REACT_APP_THIRDWEB_CLIENT_ID is not set. " +
      "On-chain minting will fail until you configure it in .env.local."
  );
}

// Polygon Amoy testnet chain
const polygonAmoy = defineChain({
  id: 80002,
  name: "Polygon Amoy",
  nativeCurrency: { name: "MATIC", symbol: "MATIC", decimals: 18 },
  rpc: "https://polygon-amoy.drpc.org",
});

const GWEI = 1_000_000_000n;
const AMOY_MIN_PRIORITY_FEE = 25n * GWEI;
const AMOY_SAFE_MAX_FEE = 60n * GWEI;

let _client = null;
let _contract = null;

function getClient() {
  if (_client) return _client;
  if (!THIRDWEB_CLIENT_ID) {
    throw new Error(
      "Thirdweb client ID not configured. " +
        "Set REACT_APP_THIRDWEB_CLIENT_ID in your .env.local file."
    );
  }
  _client = createThirdwebClient({ clientId: THIRDWEB_CLIENT_ID });
  return _client;
}

function getNftContract() {
  if (_contract) return _contract;
  const client = getClient();
  _contract = getContract({
    client,
    address: NFT_COLLECTION_ADDRESS,
    chain: polygonAmoy,
  });
  return _contract;
}

/**
 * Mint a BWK memory NFT.
 *
 * Assumes the contract has:
 *   function mintTo(address to, string uri) public returns (uint256 tokenId);
 *
 * @param {Object} params
 * @param {Object} params.account   Thirdweb connected account (from useActiveAccount())
 * @param {string} params.tokenUri  IPFS metadata URI (ipfs://CID)
 * @param {string} [params.to]      Wallet address to receive the NFT (defaults to account.address)
 *
 * @returns {Promise<{ txHash: string, tokenId: string | null }>}
 */
export async function mintMemoryOnChain({ account, tokenUri, to }) {
  if (!account) {
    throw new Error('mintMemoryOnChain: "account" (Thirdweb active account) is required');
  }
  const resolvedTo = to || account?.address;
  if (!resolvedTo) {
    throw new Error('mintMemoryOnChain: "to" address is missing and account.address is unavailable');
  }
  if (!tokenUri) {
    throw new Error('mintMemoryOnChain: "tokenUri" is required');
  }

  const client = getClient();
  const contract = getNftContract();

  console.log("[BWK][CHAIN] Preparing mintTo:", {
    to: resolvedTo,
    tokenUri,
    contract: NFT_COLLECTION_ADDRESS,
    chain: "Polygon Amoy (80002)",
  });

  const transaction = prepareContractCall({
    contract,
    method: "function mintTo(address to, string uri)",
    params: [resolvedTo, tokenUri],
    maxPriorityFeePerGas: AMOY_MIN_PRIORITY_FEE,
    maxFeePerGas: AMOY_SAFE_MAX_FEE,
  });

  // ✅ v5: must provide account here
  const sent = await sendTransaction({ transaction, account });

  const txHash =
    sent?.transactionHash ||
    sent?.hash ||
    sent?.receipt?.transactionHash ||
    null;

  if (!txHash) {
    console.warn("[BWK][CHAIN] sendTransaction returned unexpected shape:", sent);
    throw new Error("Mint sent but tx hash was not returned by Thirdweb.");
  }

  console.log("[BWK][CHAIN] Transaction sent:", txHash, "Waiting for receipt…");

  // ✅ v5 receipt wait shape
  const receipt = await waitForReceipt({
    client,
    chain: polygonAmoy,
    transactionHash: txHash,
  });

  console.log("[BWK][CHAIN] Mint transaction mined:", receipt);

  // Best-effort tokenId parsing from ERC721 Transfer logs
  let tokenId = null;
  try {
    const logs = receipt?.logs || [];
    const transferLog = logs.find((log) =>
      (log.topics || [])[0]?.toLowerCase().includes("ddf252ad")
    );
    if (transferLog && transferLog.topics?.[3]) {
      tokenId = BigInt(transferLog.topics[3]).toString();
    }
  } catch (e) {
    console.warn("[BWK][CHAIN] Could not parse tokenId from logs:", e);
  }

  return { txHash, tokenId };
}

// ==========================
// End Version 2 — mintOnChain.js
// ==========================
